import { useMemo, useState } from "react";
import { Ic } from "./NegotiationPack/shared";

const SUPPLIERS = [
  { id: 0, name: "CastPro", loc: "Monterrey, MX", cat: "Castings", spend: "$810,000", gap: "+9.4%", gapDir: "up", status: "Ready", date: "21 Aug 2026" },
  { id: 1, name: "MetalForm Solutions", loc: "Detroit, US", cat: "Stampings", spend: "$1,240,000", gap: "+12.1%", gapDir: "up", status: "Ready", date: "28 Aug 2026" },
  { id: 2, name: "PrecisionCast Ltd", loc: "London, UK", cat: "Castings", spend: "$430,000", gap: "+5.7%", gapDir: "mid", status: "In Progress", date: "3 Sep 2026" },
  { id: 3, name: "TechAlloy Corp", loc: "Munich, DE", cat: "Extrusions", spend: "$2,100,000", gap: "+15.3%", gapDir: "up", status: "Ready", date: "15 Sep 2026" },
  { id: 4, name: "FlexiMold Inc", loc: "Guadalajara, MX", cat: "Plastics", spend: "$290,000", gap: "-2.1%", gapDir: "down", status: "Not Started", date: "TBD" },
  { id: 5, name: "CoreSteel Partners", loc: "Pittsburgh, US", cat: "Raw Materials", spend: "$680,000", gap: "+7.9%", gapDir: "up", status: "In Progress", date: "TBD" },
];
const FILTERS = [{ key: "All", label: "All (6)" }, { key: "Ready", label: "Ready (3)" }, { key: "In Progress", label: "In Progress (2)" }, { key: "Not Started", label: "Not Started (1)" }];
const gapClass = (direction: string) => direction === "up" ? "nptbl__gap-value--up" : direction === "mid" ? "nptbl__gap-value--mid" : "nptbl__gap-value--down";
const dotClass = (direction: string) => direction === "up" ? "nptbl__gap-dot--up" : direction === "mid" ? "nptbl__gap-dot--mid" : "nptbl__gap-dot--down";
const statusClass: Record<string, string> = {
  Ready: "nptbl__status-badge--ready",
  "In Progress": "nptbl__status-badge--progress",
  "Not Started": "nptbl__status-badge--not-started",
};

type Supplier = typeof SUPPLIERS[number];
interface NegotiationPackTableProps { onOpen: (supplier: Supplier) => void; }

export function NegotiationPackTable({ onOpen }: NegotiationPackTableProps) {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const rows = useMemo(() => SUPPLIERS.filter((supplier) => (filter === "All" || supplier.status === filter)).filter((supplier) => {
    const normalized = query.trim().toLowerCase();
    return !normalized || [supplier.name, supplier.loc, supplier.cat].some((value) => value.toLowerCase().includes(normalized));
  }), [filter, query]);

  return (
    <div className="nptbl">
      <div className="nptbl__heading-block">
        <div className="nptbl__eyebrow">Sourcing</div>
        <div className="nptbl__title">Negotiation Pack</div>
      </div>

      <div className="nptbl__controls">
        <label className="nptbl__search">
          <Ic.search width={16} height={16} className="nptbl__search-icon" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search negotiations..."
            aria-label="Search negotiations"
            className="nptbl__search-input"
          />
        </label>

        <div className="nptbl__filters">
          {FILTERS.map((item) => (
            <button
              type="button"
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={`nptbl__filter-btn${filter === item.key ? " is-active" : ""}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="nptbl__table-wrap">
        <div className="nptbl__head-row">
          {["Supplier & Location", "Category", "Annual Spend", "Benchmark Gap", "Status", "Next Meeting", ""].map((heading) => (
            <div key={heading} className="nptbl__head-cell">{heading}</div>
          ))}
        </div>

        {rows.length === 0 && <div className="nptbl__empty">No negotiations match your search.</div>}

        {rows.map((supplier, index) => (
          <div
            key={supplier.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(supplier)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen(supplier);
              }
            }}
            className={`np-row nptbl__row${index % 2 ? " is-alt" : ""}`}
          >
            <div>
              <div className="nptbl__supplier-name">{supplier.name}</div>
              <div className="nptbl__supplier-loc">{supplier.loc}</div>
            </div>
            <div className="nptbl__cell-muted">{supplier.cat}</div>
            <div className="nptbl__cell-base">{supplier.spend}</div>
            <div className="nptbl__gap-cell">
              <span className={`nptbl__gap-dot ${dotClass(supplier.gapDir)}`} />
              <span className={`nptbl__gap-value ${gapClass(supplier.gapDir)}`}>{supplier.gap}</span>
            </div>
            <span className={`nptbl__status-badge ${statusClass[supplier.status] ?? "nptbl__status-badge--not-started"}`}>
              {supplier.status}
            </span>
            <div className="nptbl__cell-muted">{supplier.date}</div>
            <div className="viewDetails">
              <span>View Details</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
